
import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Fixture, type FixtureSelectOptions } from '../types';
import type { AuthLang } from './AuthModal';
import { t as tI18n, tLegacy } from '../utils/i18n';
import { Watermark } from './Watermark';
import { adjustImageOptimized } from '../utils/imageProcessor';
import {
  startEditorCompareSliderPointerDrag,
  startEditorCompareSliderTouchDrag,
} from '../utils/compareSliderPointer';
import { attachComparePanelPinchZoom } from '../utils/comparePanelPinchZoom';

type ManualDebugPoint = { x: number; y: number };
type ManualDebugEntry = { id: string; a: ManualDebugPoint; b: ManualDebugPoint[]; c: ManualDebugPoint[] };
type AutoCoveDebugEntry = { id: string; a: ManualDebugPoint; b: ManualDebugPoint[]; c: ManualDebugPoint[] };

/** A/B/C 调试点拖动：A/自动槽灯 B 走 onMoveFixture；其余为覆盖显示坐标 */
type DebugPointDrag =
  | { kind: 'manual'; fixtureId: string; role: 'a' }
  | { kind: 'manual'; fixtureId: string; role: 'b'; bIndex: number }
  | { kind: 'manual'; fixtureId: string; role: 'c'; cIndex: number }
  | { kind: 'autoCove'; fixtureId: string; role: 'a' }
  | { kind: 'autoCove'; fixtureId: string; role: 'b'; bIndex: number }
  | { kind: 'autoCove'; fixtureId: string; role: 'c'; cIndex: number };

type DebugPointOverrides = { a?: ManualDebugPoint; b?: ManualDebugPoint[]; c?: ManualDebugPoint[] };

const MANUAL_ADD_LIGHT_TYPES = new Set([
  'downlight',
  'cove_light',
  'cove_lighting',
]);

interface EditorProps {
  selectedFixtureIds: string[];
  fixtures: Fixture[];
  originalImage: string | null;
  renderedImage: string | null;
  updateSelectedFixtures: (updates: Partial<Fixture>) => void;
  onSelectFixture: (id: string, opts?: FixtureSelectOptions) => void;
  onAddFixture?: (fixture: Omit<Fixture, 'id'>) => void; // ?? ӹԴ
  onMoveFixture?: (id: string, x: number, y: number) => void; // ?? ƶԴ
  onToggleLock?: (id: string) => void; // ?? л״̬
  onDeleteFixtures?: (ids: string[]) => void; // ?? ɾԴ
  onUndo?: () => void; // ?? 
  isProcessing: boolean;
  isScanning: boolean;
  showOriginal: boolean;
  onSwitchToOriginal: () => void;
  onSwitchToRendered: () => void;
  isDarkMode: boolean;
  language: AuthLang;
  isEditMode?: boolean; // ?? ༭ģʽֶӵ㣩
  selectedLightSourceType?: string | null; // ?? ѡеĹԴ
  selectedLightSourceLabel?: string | null; // ?? ѡеĹԴǩ
  /**
   * 文字命令：仅文本；在已生成结果图上由 App 走 commandMode 单图渲染。
   */
  onCommandSend?: (payload: { commandText: string }) => Promise<void> | void;
  onCommandDraftChange?: (text: string) => void;
  /** 手动「光带」场景（strip_context），与识别枚举一致 */
  manualStripContext?: string;
  /** 手动「筒灯」场景（downlight_context），与识别枚举一致 */
  manualDownlightContext?: string;
  /** 主界面/文字命令渲染进度（0–100），用于底部指令条进度条 */
  renderingProgress?: number;
  renderingProgressSource?: 'generate' | 'command' | null;
  /** 识别中：并行拉取空间结构数据时显示「空间结构扫描中」 */
  recognitionScanPhase?: 'spatial' | 'full' | null;
  /** 拖拽上传图片回调 */
  onUpload?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const Editor: React.FC<EditorProps> = ({ 
  selectedFixtureIds,
  fixtures,
  originalImage, 
  renderedImage, 
  updateSelectedFixtures,
  onSelectFixture,
  onAddFixture,
  onMoveFixture,
  onToggleLock,
  onDeleteFixtures,
  onUndo,
  isProcessing,
  isScanning,
  showOriginal,
  onSwitchToOriginal,
  onSwitchToRendered,
  isDarkMode,
  language,
  isEditMode = false,
  selectedLightSourceType = null,
  selectedLightSourceLabel = null,
  manualStripContext = 'generic',
  manualDownlightContext = 'generic',
  onCommandSend,
  onCommandDraftChange,
  renderingProgress = 0,
  renderingProgressSource = null,
  recognitionScanPhase = null,
  onUpload,
}) => {
  // ?? ؼ޸༭ģʽ״̬浽ȫֱ SpotlightInteractive Է
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).__isEditMode = isEditMode;
    }
  }, [isEditMode]);
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomLevelPinchRef = useRef(1);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const imageWrapperRef = useRef<HTMLDivElement>(null);
  const originalImageRef = useRef<HTMLImageElement>(null);
  const renderedImageRef = useRef<HTMLImageElement>(null);
  const onMoveFixtureRef = useRef(onMoveFixture);
  onMoveFixtureRef.current = onMoveFixture;
  /** 拖动开始时的指针位置（避免仅 mousedown+mouseup 无 mousemove 时 lastPointer 仍为 0,0） */
  const dragPointerSeedRef = useRef({ x: 0, y: 0 });
  const debugDragPointerSeedRef = useRef({ x: 0, y: 0 });
  const [isComparing, setIsComparing] = useState(false);
  const [comparisonPos, setComparisonPos] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1); // ż
  const [panX, setPanX] = useState(0); // ˮƽƽ
  const [panY, setPanY] = useState(0); // ֱƽ
  const [isPanning, setIsPanning] = useState(false); // Ƿƽ
  const [panStart, setPanStart] = useState({ x: 0, y: 0 }); // ƽʼλ
  const [imageBounds, setImageBounds] = useState<{left: number, top: number, width: number, height: number} | null>(null); // ͼƬʵʾ
  // ?? ֶӺƶԴ״̬
  const [draggingFixtureId, setDraggingFixtureId] = useState<string | null>(null); // קĹԴID
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 }); // קʼλ
  // A/B/C 调试（仅用于“增加光源”8种灯），通过 window.__NEMO_MANUAL_LIGHT_DEBUG_8 开关
  const [manualDebugEnabled, setManualDebugEnabled] = useState(false);
  const [manualDebugEntries, setManualDebugEntries] = useState<ManualDebugEntry[]>([]);
  // A/B/C 调试（仅用于“识别出的槽灯/暗光带”），通过 window.__NEMO_AUTO_COVE_DEBUG 开关
  const [autoCoveDebugEnabled, setAutoCoveDebugEnabled] = useState(false);
  const [autoCoveDebugEntries, setAutoCoveDebugEntries] = useState<AutoCoveDebugEntry[]>([]);
  const [debugPointDrag, setDebugPointDrag] = useState<DebugPointDrag | null>(null);
  const [manualDebugOverrides, setManualDebugOverrides] = useState<Record<string, DebugPointOverrides>>({});
  const [autoCoveDebugOverrides, setAutoCoveDebugOverrides] = useState<Record<string, DebugPointOverrides>>({});
  const manualDebugEntriesRef = useRef(manualDebugEntries);
  manualDebugEntriesRef.current = manualDebugEntries;
  const autoCoveDebugEntriesRef = useRef(autoCoveDebugEntries);
  autoCoveDebugEntriesRef.current = autoCoveDebugEntries;

  useEffect(() => {
    if (!manualDebugEnabled) setManualDebugOverrides({});
  }, [manualDebugEnabled]);
  useEffect(() => {
    if (!autoCoveDebugEnabled) setAutoCoveDebugOverrides({});
  }, [autoCoveDebugEnabled]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const readFlag = () => !!(window as any).__NEMO_MANUAL_LIGHT_DEBUG_8;
    setManualDebugEnabled(readFlag());
    const timer = window.setInterval(() => {
      setManualDebugEnabled((prev) => {
        const next = readFlag();
        return prev === next ? prev : next;
      });
    }, 400);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const readFlag = () => !!(window as any).__NEMO_AUTO_COVE_DEBUG;
    setAutoCoveDebugEnabled(readFlag());
    const timer = window.setInterval(() => {
      setAutoCoveDebugEnabled((prev) => {
        const next = readFlag();
        return prev === next ? prev : next;
      });
    }, 400);
    return () => window.clearInterval(timer);
  }, []);

  // ========= Chat / Command =========
  const [chatCommand, setChatCommand] = useState<string>('');
  const tK = (key: Parameters<typeof tI18n>[0]) => tI18n(key, language);

  /** 与 App.handleChatCommandSend 一致：仅在有独立「生成结果图」（与原图不同）时解锁文字命令 */
  const textCommandUnlocked =
    !!renderedImage && !!originalImage && renderedImage !== originalImage;

  useEffect(() => {
    if (textCommandUnlocked) return;
    setChatCommand('');
    onCommandDraftChange?.('');
  }, [textCommandUnlocked, onCommandDraftChange]);

  const handleChatSend = () => {
    if (!textCommandUnlocked) return;
    const cmd = chatCommand.trim();
    if (!cmd) return;

    const doSend = async () => {
      try {
        await onCommandSend?.({ commandText: cmd });
      } catch (e: any) {
        alert(e?.message ? String(e.message) : tK('command_hint_failed'));
        return;
      } finally {
        setChatCommand('');
        onCommandDraftChange?.('');
      }
    };

    void doSend();
  };

  // 键盘回车也可执行命令（焦点不在输入框时）
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.isComposing) return;
      if (e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
      const target = e.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || target.isContentEditable) return;
      }
      if (!textCommandUnlocked || !chatCommand.trim() || isProcessing || isScanning) return;
      e.preventDefault();
      handleChatSend();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [chatCommand, isProcessing, isScanning, textCommandUnlocked]);

  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const dragCounterRef = useRef(0);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      const hasFiles = Array.from(e.dataTransfer.items).some(item => item.kind === 'file');
      if (hasFiles) {
        setIsDraggingFile(true);
      }
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDraggingFile(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // 设置拖拽效果为 copy
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingFile(false);
    dragCounterRef.current = 0;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      // 检查是否为图片文件
      if (!file.type.startsWith('image/')) {
        alert(language !== 'zh' ? 'Please drop an image file.' : '请拖入图片文件。');
        return;
      }
      // 检查是否为 GIF
      if (file.type === 'image/gif') {
        alert(language !== 'zh' ? 'GIF files are not supported.' : '不支持 GIF 文件。');
        return;
      }
      
      // 创建合成事件对象以兼容 onUpload 接口
      const syntheticEvent = {
        target: {
          files: files,
          value: '',
        },
        currentTarget: {
          files: files,
          value: '',
        },
        preventDefault: () => {},
        stopPropagation: () => {},
        nativeEvent: new Event('drop'),
        type: 'change',
        bubbles: true,
        cancelable: true,
        timeStamp: Date.now(),
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      
      onUpload?.(syntheticEvent);
    }
  };

  /** 与侧边栏语种一致：告知浏览器/IME 当前输入语言，避免界面是韩/日/英时仍按中文输入上下文处理 */
  const commandInputLangTag: Record<AuthLang, string> = {
    en: 'en',
    zh: 'zh-Hans',
  };
  const commandInputLang = commandInputLangTag[language];
  /** 英文开拼写检查；中文交给系统 IME */
  const commandInputSpellCheck = language === 'en';

  const renderChatDialog = (bottomClass: string) => (
    <>
      <div
        lang={commandInputLang}
        className={`absolute ${bottomClass} left-1/2 -translate-x-1/2 z-20 w-[960px] max-w-[92%] rounded-[14px] border backdrop-blur-md px-4 py-1.5 ${
          isDarkMode ? 'bg-[#0A0A0A]/55 border-[#262626]' : 'bg-white/60 border-zinc-200'
        }`}
      >
        <div className="flex items-center gap-1.5 min-h-[48px] w-full">
          <input
            key={`nemo-chat-command-input-${language}`}
            type="text"
            enterKeyHint="send"
            lang={commandInputLang}
            spellCheck={commandInputSpellCheck}
            autoCapitalize="sentences"
            value={chatCommand}
            onChange={(e) => {
              setChatCommand(e.target.value);
              onCommandDraftChange?.(e.target.value);
            }}
            placeholder={tK('text_command_placeholder')}
            className={`flex-1 min-w-0 h-10 rounded-md border-0 px-3 py-0 leading-10 outline-none transition-colors ${
              isDarkMode
                ? 'bg-transparent text-white placeholder:text-neutral-500'
                : 'bg-transparent text-zinc-900 placeholder:text-zinc-400'
            }`}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleChatSend();
              }
            }}
          />

          <div className="flex h-10 shrink-0 items-center">
            <button
              type="button"
              onClick={handleChatSend}
              disabled={!chatCommand.trim() || isProcessing || isScanning}
              aria-label={tK('command_send')}
              className={`h-10 w-10 shrink-0 flex items-center justify-center rounded-md border-0 bg-transparent transition-opacity ${
                isDarkMode
                  ? 'text-neutral-300 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed'
                  : 'text-zinc-800 hover:bg-black/5 disabled:opacity-30 disabled:cursor-not-allowed'
              }`}
            >
              <svg
                className="size-[1.38rem] shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M11.4697 3.46967C11.7626 3.17678 12.2374 3.17678 12.5303 3.46967L18.5303 9.46967C18.8232 9.76256 18.8232 10.2374 18.5303 10.5303C18.2374 10.8232 17.7626 10.8232 17.4697 10.5303L12.75 5.81066L12.75 20C12.75 20.4142 12.4142 20.75 12 20.75C11.5858 20.75 11.25 20.4142 11.25 20L11.25 5.81066L6.53033 10.5303C6.23744 10.8232 5.76256 10.8232 5.46967 10.5303C5.17678 10.2374 5.17678 9.76256 5.46967 9.46967L11.4697 3.46967Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
        </div>

        {renderingProgressSource === 'command' && (isProcessing || renderingProgress > 0) && (
          <div className="mt-2 space-y-1">
            <div className={`h-1 w-full rounded-full overflow-hidden ${isDarkMode ? 'bg-white/10' : 'bg-black/10'}`}>
              <div
                className="h-full rounded-full bg-[#D7CDA1] transition-[width] duration-300 ease-out"
                style={{ width: `${Math.min(100, Math.max(0, renderingProgress))}%` }}
              />
            </div>
            <p className={`text-[11px] tabular-nums min-h-[1rem] ${isDarkMode ? 'text-neutral-400' : 'text-zinc-600'}`}>
              {renderingProgress >= 100
                ? tK('command_render_complete')
                : isProcessing || renderingProgress > 0
                  ? `${Math.max(1, Math.round(Math.min(100, renderingProgress)))}%`
                  : '\u00a0'}
            </p>
          </div>
        )}
      </div>
    </>
  );

  // ˳Աģʽʱźƽ
  useEffect(() => {
    if (!isComparing) {
      setZoomLevel(1);
      setPanX(0);
      setPanY(0);
      setImageBounds(null);
    }
  }, [isComparing]);

  // ͼƬ߽磨ڻ˶λͬͼ
  useEffect(() => {
    if (!isComparing || !renderedImage) return;
    
    const updateImageBounds = () => {
      if (originalImageRef.current && containerRef.current) {
        const imageRect = originalImageRef.current.getBoundingClientRect();
        const containerRect = containerRef.current.getBoundingClientRect();
        setImageBounds({
          left: imageRect.left - containerRect.left,
          top: imageRect.top - containerRect.top,
          width: imageRect.width,
          height: imageRect.height,
        });
      }
    };

    // ?? ؼȷͼȹ̶ΪȣֲխСο2.txt117У
    const updateRenderedImageWidth = () => {
      if (renderedImageRef.current && originalImageRef.current) {
        const rendered = renderedImageRef.current;
        const o = originalImageRef.current;
        // ̶Ϊȣο2.txtʵ֣
        rendered.style.width = `${o.offsetWidth}px`;
        rendered.style.height = `${o.offsetHeight}px`;
        rendered.style.maxWidth = 'none';
        rendered.style.maxHeight = 'none';
      }
    };

    // ʼ
    updateImageBounds();
    updateRenderedImageWidth();

    // ڴС仯 - ο2.txt131-134
    const handleResize = () => {
      updateImageBounds();
      updateRenderedImageWidth();
      // ȷͼʼյ
      if (imageWrapperRef.current && renderedImageRef.current && originalImageRef.current) {
        const r = renderedImageRef.current;
        const o = originalImageRef.current;
        r.style.width = `${o.offsetWidth}px`;
        r.style.height = `${o.offsetHeight}px`;
      }
    };
    window.addEventListener('resize', handleResize);
    
    // ʹ MutationObserver ͼƬߴ仯
    const observer = new MutationObserver(() => {
      updateImageBounds();
      updateRenderedImageWidth();
    });
    if (originalImageRef.current) {
      observer.observe(originalImageRef.current, {
        attributes: true,
        attributeFilter: ['style', 'width', 'height']
      });
    }
    if (imageWrapperRef.current) {
      observer.observe(imageWrapperRef.current, {
        attributes: true,
        attributeFilter: ['style', 'width', 'height']
      });
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, [isComparing, renderedImage, zoomLevel, panX, panY]);

  const t = (en: string, zh: string) => tLegacy(en, zh, language);

  const primaryFixture = fixtures.find(f => f.id === selectedFixtureIds[0]) || null;

  // ?? ״ֵֶ̬룩
  const [intensityInput, setIntensityInput] = useState<string>('');
  const [colorTempInput, setColorTempInput] = useState<string>('');
  const [hueInput, setHueInput] = useState<string>('');

  // ??  primaryFixture 仯ʱֵͬ
  useEffect(() => {
    if (primaryFixture) {
      setIntensityInput(primaryFixture.intensity === 0 ? '0' : Math.round(primaryFixture.intensity * 100).toString());
      const temp = primaryFixture.colorTemp;
      if (temp === 0) {
        setColorTempInput('6500');
      } else if (temp < 0) {
        setColorTempInput(Math.round(6500 + (temp * 90)).toString());
      } else {
        setColorTempInput(Math.round(6500 + (temp * 170)).toString());
      }
      setHueInput((primaryFixture.hue || 0).toString());
    } else {
      setIntensityInput('0');
      setColorTempInput('6500');
      setHueInput('0');
    }
  }, [primaryFixture?.id, primaryFixture?.intensity, primaryFixture?.colorTemp, primaryFixture?.hue]);




  useEffect(() => {
    zoomLevelPinchRef.current = zoomLevel;
  }, [zoomLevel]);

  // ŹܣڶԱģʽ£
  useEffect(() => {
    if (!isComparing || !containerRef.current) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const newZoom = Math.max(0.5, Math.min(5, zoomLevel + delta));
      setZoomLevel(newZoom);
      
      // С1ƽ
      if (newZoom <= 1) {
        setPanX(0);
        setPanY(0);
      }
    };

    const container = containerRef.current;
    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [isComparing, zoomLevel]);

  useEffect(() => {
    if (!isComparing || !containerRef.current) return;
    return attachComparePanelPinchZoom(containerRef.current, {
      getZoom: () => zoomLevelPinchRef.current,
      setZoom: (z) => {
        zoomLevelPinchRef.current = z;
        setZoomLevel(z);
        if (z <= 1) {
          setPanX(0);
          setPanY(0);
        }
      },
      shouldIgnoreTouchTarget: (t) =>
        !!(t && typeof (t as HTMLElement).closest === 'function' && (t as HTMLElement).closest('.slider-handle')),
    });
  }, [isComparing]);

  // ס϶ƶ棨ڶԱģʽ£
  useEffect(() => {
    if (!isComparing || !containerRef.current || zoomLevel <= 1) return;

    const handleMouseDown = (e: MouseEvent) => {
      // ֻӦҲڻ
      const target = e.target as HTMLElement;
      if (target.closest('.cursor-ew-resize') || target.closest('.group')) return;
      
      if (e.button === 0) { // 
        e.preventDefault();
        setIsPanning(true);
        setPanStart({ x: e.clientX - panX, y: e.clientY - panY });
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isPanning) return;
      
      const newPanX = e.clientX - panStart.x;
      const newPanY = e.clientY - panStart.y;
      
      // ƽƵÿһ䣬ƷΧ
      setPanX(newPanX);
      setPanY(newPanY);
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (e.button === 0) { // 
        setIsPanning(false);
      }
    };

    const container = containerRef.current;
    container.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isComparing, zoomLevel, isPanning, panStart, panX, panY]);

  // ?? ͼƬʵʾ껻㣩
  const getImageDisplayRatio = (): {
    scaleX: number;
    scaleY: number;
    naturalWidth: number;
    naturalHeight: number;
    displayWidth: number;
    displayHeight: number;
    contentLeft: number;
    contentTop: number;
    contentWidth: number;
    contentHeight: number;
    containerRelativeLeft: number;
    containerRelativeTop: number;
  } => {
    if (!originalImageRef.current) {
      return {
        scaleX: 1,
        scaleY: 1,
        naturalWidth: 1000,
        naturalHeight: 1000,
        displayWidth: 1000,
        displayHeight: 1000,
        contentLeft: 0,
        contentTop: 0,
        contentWidth: 1000,
        contentHeight: 1000,
        containerRelativeLeft: 0,
        containerRelativeTop: 0,
      };
    }
    
    const img = originalImageRef.current;
    const naturalWidth = img.naturalWidth || 1000;
    const naturalHeight = img.naturalHeight || 1000;
    const imgRect = img.getBoundingClientRect();
    const containerRect = img.parentElement?.getBoundingClientRect() || imgRect;
    
    // ?? ؼ޸ object-fit: contain ĺڱ
    // ͼƬʹ object-fit: contain ʱʵͼƬݿܲռ
    const containerAspect = containerRect.width / containerRect.height;
    const imageAspect = naturalWidth / naturalHeight;
    
    let contentWidth: number, contentHeight: number, contentLeft: number, contentTop: number;
    
    // ?? ؼ޸ͼƬ object-fit: contain ģʽµʵʾߴƫ
    // ע⣺imgRect ͼƬԪصʵʾ򣨿ܰڱߣcontainerRect 
    // ҪͼƬڵʵλãųڱߣ
    
    if (imageAspect > containerAspect) {
      // ͼƬкڱߣ
      contentWidth = containerRect.width;
      contentHeight = containerRect.width / imageAspect;
      // contentLeft ƫƣͼƬڵƫƣ
      contentLeft = 0; // ҾУͼƬƫΪ0
      contentTop = (containerRect.height - contentHeight) / 2; // ¾
    } else {
      // ͼƬߣкڱߣ
      contentWidth = containerRect.height * imageAspect;
      contentHeight = containerRect.height;
      // contentLeft ƫƣͼƬڵƫƣ
      contentLeft = (containerRect.width - contentWidth) / 2; // Ҿ
      contentTop = 0; // ¾УͼƬߣƫΪ0
    }
    
    // ?? ؼ޸contentLeft/contentTop ƫ
    // Ҫֵں
    const containerRelativeLeft = contentLeft;
    const containerRelativeTop = contentTop;
    
    // תΪӿڵľ꣨¼
    contentLeft = containerRect.left + contentLeft;
    contentTop = containerRect.top + contentTop;
    
    const displayWidth = contentWidth;
    const displayHeight = contentHeight;
    
    // ʾ
    const scaleX = displayWidth / naturalWidth;
    const scaleY = displayHeight / naturalHeight;
    
    // ?? ؼϢӡ Natural vs Client ߴԱȣڳߴ仯ʱӡ־
    // ʹþ̬ϴεĳߴ磬ֻڱ仯ʱӡ
    if (typeof window !== 'undefined' && !(window as any).__lastImageSize) {
      (window as any).__lastImageSize = { naturalWidth: 0, naturalHeight: 0, contentWidth: 0, contentHeight: 0 };
    }
    
    const lastSize = (window as any).__lastImageSize || { naturalWidth: 0, naturalHeight: 0, contentWidth: 0, contentHeight: 0 };
    const sizeChanged = 
      lastSize.naturalWidth !== naturalWidth || 
      lastSize.naturalHeight !== naturalHeight || 
      Math.abs(lastSize.contentWidth - contentWidth) > 1 || 
      Math.abs(lastSize.contentHeight - contentHeight) > 1;
    
    if (sizeChanged) {
      const imgClientWidth = img.clientWidth || 0;
      const imgClientHeight = img.clientHeight || 0;
      const imgOffsetWidth = img.offsetWidth || 0;
      const imgOffsetHeight = img.offsetHeight || 0;
      
      console.log('[坐标换算] 图片显示信息尺寸变化:', {
        naturalSize: `${naturalWidth}x${naturalHeight}`,
        clientSize: `${imgClientWidth}x${imgClientHeight}`,
        offsetSize: `${imgOffsetWidth}x${imgOffsetHeight}`,
        containerSize: `${containerRect.width.toFixed(1)}x${containerRect.height.toFixed(1)}`,
        contentSize: `${contentWidth.toFixed(1)}x${contentHeight.toFixed(1)}`,
        contentOffset: `(${contentLeft.toFixed(1)}, ${contentTop.toFixed(1)})`,
        scale: `${scaleX.toFixed(3)}x${scaleY.toFixed(3)}`,
        imageAspect: imageAspect.toFixed(3),
        containerAspect: containerAspect.toFixed(3),
        hasPadding: imageAspect !== containerAspect ? '是（有黑边）' : '否（无黑边）'
      });
      
      // »ĳߴ
      (window as any).__lastImageSize = { naturalWidth, naturalHeight, contentWidth, contentHeight };
    }
    
    return { 
      scaleX, 
      scaleY, 
      naturalWidth, 
      naturalHeight, 
      displayWidth, 
      displayHeight, 
      contentLeft, // ӿڵľ
      contentTop,  // ӿڵľ
      contentWidth, 
      contentHeight,
      containerRelativeLeft, // ƫʾλü㣩
      containerRelativeTop    // ƫʾλü㣩
    };
  };

  // ?? ͼƬ¼ӹԴ
  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    console.log('[点击事件] 参数检查:', {
      isEditMode,
      selectedLightSourceType,
      hasOriginalImage: !!originalImageRef.current,
      hasOnAddFixture: !!onAddFixture,
      target: e.target,
      currentTarget: e.currentTarget
    });

    // ֻڱ༭ģʽѡ˹Դʱ
    if (!isEditMode) {
      console.log('[点击事件] 当前不是编辑模式');
      return;
    }
    
    if (!selectedLightSourceType) {
      console.log('[点击事件] 未选择光源类型');
      return;
    }
    
    if (!originalImageRef.current) {
      console.log('[点击事件] 原图未加载');
      return;
    }
    
    if (!onAddFixture) {
      console.log('[点击事件] onAddFixture 回调不存在');
      return;
    }

    // ֹ¼ðݣⴥ¼
    e.stopPropagation();
    e.preventDefault();

    const img = originalImageRef.current;
    
    // ?? ȡͼƬʵȾϢ
    // contentLeft/contentTop Ѿӿڵľ꣨ getImageDisplayRatio ת
    const { contentLeft, contentTop, contentWidth, contentHeight, naturalWidth, naturalHeight } = getImageDisplayRatio();
    
    // ?? ؼ޸ʹȷתʽ
    // x_normalized = (x_event - contentLeft) / contentWidth
    // contentLeft Ѿӿڵľֱ꣬ʹ
    const clickX = e.clientX - contentLeft;
    const clickY = e.clientY - contentTop;
    
    console.log('[点击事件] 点击位置:', {
      clientXY: `(${e.clientX}, ${e.clientY})`,
      contentOffset: `(${contentLeft.toFixed(1)}, ${contentTop.toFixed(1)})`,
      clickXY: `(${clickX.toFixed(1)}, ${clickY.toFixed(1)})`,
      contentSize: `${contentWidth.toFixed(1)}x${contentHeight.toFixed(1)}`,
      naturalSize: `${naturalWidth}x${naturalHeight}`
    });
    
    // ?? תΪһ꣨0-1000ʹʵߴ
    // ȷ߽룺 normalizedX = 1000 ʱȷͼƬҲرԵ
    const normalizedX = Math.max(0, Math.min(1000, (clickX / contentWidth) * 1000));
    const normalizedY = Math.max(0, Math.min(1000, (clickY / contentHeight) * 1000));
    
    // Ѿ˱߽봦ֱʹ
    const clampedX = normalizedX;
    const clampedY = normalizedY;
    
    // ¹Դ
    const newFixture: Omit<Fixture, 'id'> = {
      x: clampedX,
      y: clampedY,
      type: selectedLightSourceType as any,
      label: (selectedLightSourceLabel || selectedLightSourceType || 'Light').trim() || 'Light',
      intensity: 1.0,
      colorTemp: 0,
      hue: 0,
      isModified: false,
      source: 'manual' as const, // ?? Ϊֶ
      locked: false, // ?? ֶӵĵĬδ
      // 射灯/筒灯都使用默认下照参数，避免方向不稳定
      ...((selectedLightSourceType === 'spotlight' || selectedLightSourceType === 'downlight') && {
        rotation: 90,   // Ĭ
        beamAngle: 24   // 默认24°，更接近真实筒灯/射灯下照
      }),
      ...(selectedLightSourceType === 'cove_light' && {
        stripContext: manualStripContext,
      }),
      ...(selectedLightSourceType === 'downlight' && {
        downlightContext: manualDownlightContext,
      }),
    };
    
    console.log('[点击事件] 新增手动光源:', newFixture);
    onAddFixture(newFixture);
  };

  // ?? Դק
  const handleFixtureMouseDown = (e: React.MouseEvent, fixtureId: string, isLocked: boolean, isManualAdded: boolean) => {
    // ?? ޸ק
    // 1. ֶӵĹ㣺Ƿڱ༭ģʽֻҪδͿק
    // 2. AIʶĹ㣺ֻҪδͿק󼴿ƶ༭ģʽ
    if (!onMoveFixture || !originalImageRef.current) {
      console.log('[拖拽] onMoveFixture 或 originalImageRef 不存在');
      return;
    }
    
    // ?? ק
    if (isLocked) {
      console.log('[拖拽] 光源已锁定，无法拖拽（可双击切换）');
      return;
    }
    
    // ?? ֻҪδֶӻAIʶ𣬶ק
    console.log('[拖拽] 开始拖拽光源:', fixtureId, '手动添加:', isManualAdded, '编辑模式:', isEditMode, '已锁定:', isLocked);
    
    e.stopPropagation();
    e.preventDefault();
    
    setDebugPointDrag(null);
    dragPointerSeedRef.current = { x: e.clientX, y: e.clientY };
    setDraggingFixtureId(fixtureId);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  // ?? ˫л״̬
  const handleFixtureDoubleClick = (e: React.MouseEvent, fixtureId: string) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (onToggleLock) {
      onToggleLock(fixtureId);
      console.log('切换光源锁定状态:', fixtureId);
    }
  };

  // ?? ȫƶקԴ㣩
  useEffect(() => {
    if (!draggingFixtureId) return;

    let rafId = 0;
    const lastPointer = {
      x: dragPointerSeedRef.current.x,
      y: dragPointerSeedRef.current.y,
    };

    const applyFromPointer = () => {
      const move = onMoveFixtureRef.current;
      if (!move || !originalImageRef.current) return;
      const { contentLeft, contentTop, contentWidth, contentHeight } = getImageDisplayRatio();
      const mouseX = lastPointer.x - contentLeft;
      const mouseY = lastPointer.y - contentTop;
      const normalizedX = Math.max(0, Math.min(1000, (mouseX / contentWidth) * 1000));
      const normalizedY = Math.max(0, Math.min(1000, (mouseY / contentHeight) * 1000));
      move(draggingFixtureId, normalizedX, normalizedY);
    };

    const flushMove = () => {
      rafId = 0;
      applyFromPointer();
    };

    const handleMouseMove = (e: MouseEvent) => {
      lastPointer.x = e.clientX;
      lastPointer.y = e.clientY;
      if (!onMoveFixtureRef.current || !originalImageRef.current) return;
      if (rafId) return;
      rafId = requestAnimationFrame(flushMove);
    };

    const handleMouseUp = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      applyFromPointer();
      setDraggingFixtureId(null);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingFixtureId]);


  // A/B/C 调试点拖动（与主光点拖动互斥）
  useEffect(() => {
    if (!debugPointDrag) return;
    const drag = debugPointDrag;
    let rafId = 0;
    const lastPointer = {
      x: debugDragPointerSeedRef.current.x,
      y: debugDragPointerSeedRef.current.y,
    };

    const applyFromPointer = () => {
      if (!originalImageRef.current) return;
      const { contentLeft, contentTop, contentWidth, contentHeight, naturalWidth, naturalHeight } =
        getImageDisplayRatio();
      const mx = lastPointer.x - contentLeft;
      const my = lastPointer.y - contentTop;
      const nx = Math.max(0, Math.min(1000, (mx / contentWidth) * 1000));
      const ny = Math.max(0, Math.min(1000, (my / contentHeight) * 1000));
      const px = Math.max(0, Math.min(naturalWidth, (mx / contentWidth) * naturalWidth));
      const py = Math.max(0, Math.min(naturalHeight, (my / contentHeight) * naturalHeight));

      if (drag.kind === 'manual') {
        if (drag.role === 'a') {
          onMoveFixtureRef.current?.(drag.fixtureId, nx, ny);
        } else if (drag.role === 'b') {
          setManualDebugOverrides((prev) => {
            const entry = manualDebugEntriesRef.current.find((e) => e.id === drag.fixtureId);
            if (!entry) return prev;
            const curO = prev[drag.fixtureId] || {};
            const baseB = curO.b ?? entry.b;
            if (!baseB[drag.bIndex]) return prev;
            const nextB = baseB.map((p, i) => (i === drag.bIndex ? { x: px, y: py } : p));
            return { ...prev, [drag.fixtureId]: { ...curO, b: nextB } };
          });
        } else {
          setManualDebugOverrides((prev) => {
            const entry = manualDebugEntriesRef.current.find((e) => e.id === drag.fixtureId);
            if (!entry) return prev;
            const curO = prev[drag.fixtureId] || {};
            const baseC = curO.c ?? entry.c;
            if (!baseC[drag.cIndex]) return prev;
            const nextC = baseC.map((p, i) => (i === drag.cIndex ? { x: px, y: py } : p));
            return { ...prev, [drag.fixtureId]: { ...curO, c: nextC } };
          });
        }
      } else if (drag.kind === 'autoCove') {
        if (drag.role === 'a') {
          setAutoCoveDebugOverrides((prev) => ({
            ...prev,
            [drag.fixtureId]: { ...prev[drag.fixtureId], a: { x: px, y: py } },
          }));
        } else if (drag.role === 'b') {
          onMoveFixtureRef.current?.(drag.fixtureId, nx, ny);
        } else {
          setAutoCoveDebugOverrides((prev) => {
            const entry = autoCoveDebugEntriesRef.current.find((e) => e.id === drag.fixtureId);
            if (!entry) return prev;
            const curO = prev[drag.fixtureId] || {};
            const baseC = curO.c ?? entry.c;
            if (!baseC[drag.cIndex]) return prev;
            const nextC = baseC.map((p, i) => (i === drag.cIndex ? { x: px, y: py } : p));
            return { ...prev, [drag.fixtureId]: { ...curO, c: nextC } };
          });
        }
      }
    };

    const flushMove = () => {
      rafId = 0;
      applyFromPointer();
    };

    const handleMouseMove = (e: MouseEvent) => {
      lastPointer.x = e.clientX;
      lastPointer.y = e.clientY;
      if (!originalImageRef.current) return;
      if (rafId) return;
      rafId = requestAnimationFrame(flushMove);
    };

    const handleMouseUp = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      applyFromPointer();
      setDebugPointDrag(null);
    };

      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [debugPointDrag]);

  // ?? ¼DeleteɾԴ㣬Ctrl+Z
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 룬ɾͳ
      const activeElement = document.activeElement;
      if (activeElement && (
        activeElement.tagName === 'INPUT' || 
        activeElement.tagName === 'TEXTAREA' ||
        (activeElement as HTMLElement).isContentEditable
      )) {
        return;
      }

      // ?? Ctrl+ZWindows/LinuxCmd+ZMac
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        if (onUndo) {
          onUndo();
        }
        return;
      }

      // DeleteBackspace
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedFixtureIds.length > 0 && onDeleteFixtures) {
        e.preventDefault();
        e.stopPropagation();
        
        // ǷĹԴ
        const fixturesToDelete = fixtures.filter(f => selectedFixtureIds.includes(f.id));
        const lockedFixtures = fixturesToDelete.filter(f => {
          const isLocked = (f as any).locked !== undefined 
            ? (f as any).locked 
            : ((f as any).source === 'auto' || (f as any).source === undefined);
          return isLocked;
        });

        if (lockedFixtures.length > 0) {
          // ĹԴ㣬ʾû
          const message = language === 'en' 
            ? `Cannot delete locked light sources. Double-click to unlock them first.`
            : `无法删除已锁定的光源点，请先双击解锁`;
          alert(message);
          return;
        }

        // ɾѡеĹԴ
        console.log('删除光源点:', selectedFixtureIds);
        onDeleteFixtures(selectedFixtureIds);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedFixtureIds, fixtures, onDeleteFixtures, onUndo, language]);

  /** HSL → RGB（H 0-360, S/L 0-100），用于画布光源预览与饱和度/亮度一致 */
    const hslToRgb = (h: number, s: number, l: number) => {
      const H = ((h % 360) + 360) % 360 / 60;
      const S = Math.max(0, Math.min(100, s)) / 100;
      const L = Math.max(0, Math.min(100, l)) / 100;
      const C = (1 - Math.abs(2 * L - 1)) * S;
      const X = C * (1 - Math.abs((H % 2) - 1));
      const m = L - C / 2;
      let r = 0, g = 0, b = 0;
      if (H < 1) { r = C; g = X; b = 0; } else if (H < 2) { r = X; g = C; b = 0; } else if (H < 3) { r = 0; g = C; b = X; } else if (H < 4) { r = 0; g = X; b = C; } else if (H < 5) { r = X; g = 0; b = C; } else { r = C; g = 0; b = X; }
      return { r: Math.round((r + m) * 255), g: Math.round((g + m) * 255), b: Math.round((b + m) * 255) };
    };

  const renderLightStyles = (f: Fixture) => {
    if (!f.isModified || f.intensity === 0) return null;

    let r = 255, g = 255, b = 255;
    if (f.hue !== undefined && f.hue !== null && f.hue !== 0) {
      const sat = f.saturation ?? 100;
      const light = f.lightness ?? 50;
      const rgb = hslToRgb(f.hue, sat, light);
      r = rgb.r; g = rgb.g; b = rgb.b;
    } else if (f.colorTemp > 0) { 
      const factor = f.colorTemp / 50;
      r = Math.round(255 - 160 * factor);
      g = Math.round(255 - 40 * factor);
      b = 255;
    } else if (f.colorTemp < 0) {
      const factor = Math.abs(f.colorTemp) / 50;
      r = 255;
      g = Math.round(255 - 70 * factor);
      b = Math.round(255 - 190 * factor);
    }
    
    const intensity = f.intensity;
    const coreAlpha = Math.min(0.9, intensity * 0.7);
    const volumeAlpha = Math.min(0.4, intensity * 0.3);
    const size = 300 + intensity * 400;

    const ratio = getImageDisplayRatio();
    const wrapperRect = imageWrapperRef.current?.getBoundingClientRect();
    const wrapperLeft = wrapperRect?.left ?? 0;
    const wrapperTop = wrapperRect?.top ?? 0;
    const { x: naturalX, y: naturalY } = getFixtureOriginalPixel(f);
    const displayX = naturalX * ratio.scaleX;
    const displayY = naturalY * ratio.scaleY;

    // 坐标相对 imageWrapperRef（光点绝对定位的真实基准）
    const left = `${(ratio.contentLeft - wrapperLeft + displayX).toFixed(3)}px`;
    const top = `${(ratio.contentTop - wrapperTop + displayY).toFixed(3)}px`;

    return (
      <React.Fragment key={f.id}>
        <div style={{
          position: 'absolute', left, top,
          width: `${size}px`, height: `${size}px`,
          transform: 'translate(-50%, -50%)',
          zIndex: 10,
          background: `radial-gradient(circle, rgba(${r},${g},${b},${volumeAlpha}) 0%, rgba(${r},${g},${b},${volumeAlpha * 0.5}) 25%, rgba(${r},${g},${b},${volumeAlpha * 0.1}) 60%, transparent 85%)`,
          mixBlendMode: 'screen', filter: 'blur(40px)', pointerEvents: 'none'
        }} />
        <div style={{
          position: 'absolute', left, top,
          width: `${size * 0.4}px`, height: `${size * 0.4}px`,
          transform: 'translate(-50%, -50%)',
          zIndex: 11,
          background: `radial-gradient(circle, rgba(${r},${g},${b},${coreAlpha}) 0%, rgba(${r},${g},${b},0) 70%)`,
          mixBlendMode: 'plus-lighter', filter: 'blur(20px)', pointerEvents: 'none'
        }} />
      </React.Fragment>
    );
  };

  const toDisplayPosFromOriginalPixel = (xPx: number, yPx: number) => {
    const ratio = getImageDisplayRatio();
    const wrapperRect = imageWrapperRef.current?.getBoundingClientRect();
    const wrapperLeft = wrapperRect?.left ?? 0;
    const wrapperTop = wrapperRect?.top ?? 0;
    const left = ratio.contentLeft - wrapperLeft + xPx * ratio.scaleX;
    const top = ratio.contentTop - wrapperTop + yPx * ratio.scaleY;
    return { left, top };
  };

  const getFixtureOriginalPixel = (f: Fixture) => {
    const ratio = getImageDisplayRatio();
    const fallbackNormalizedX = f.x > 1000 ? (f.x / Math.max(1, ratio.naturalWidth)) * 1000 : f.x;
    const fallbackNormalizedY = f.y > 1000 ? (f.y / Math.max(1, ratio.naturalHeight)) * 1000 : f.y;
    const fallbackX = (fallbackNormalizedX / 1000) * ratio.naturalWidth;
    const fallbackY = (fallbackNormalizedY / 1000) * ratio.naturalHeight;
    const rawX = Number(f.x);
    const rawY = Number(f.y);
    const isRefinedAutoAnchor = (f as any)._anchorRefined === true;
    const sourceManual = String((f as any).source || '').toLowerCase() === 'manual';

    // 识别灯具已完成“亮度质心微调”时，优先使用校正后的原图像素坐标，避免被模型原始坐标覆盖
    if (isRefinedAutoAnchor && Number.isFinite(rawX) && Number.isFinite(rawY)) {
      return {
        x: Math.max(0, Math.min(ratio.naturalWidth, rawX)),
        y: Math.max(0, Math.min(ratio.naturalHeight, rawY)),
      };
    }

    // 手动添加：界面与拖动使用 0–1000 原图归一化 x/y，不能用首次落点时的 _modelNormalized（移动后未同步会“拖不动”）
    if (sourceManual) {
      return {
        x: Math.max(0, Math.min(ratio.naturalWidth, fallbackX)),
        y: Math.max(0, Math.min(ratio.naturalHeight, fallbackY)),
      };
    }

    const meta = (f as any)._analysisCoordMeta as
      | { base64Width: number; base64Height: number; offsetX: number; offsetY: number; scale: number }
      | undefined;
    const modelX = Number((f as any)._modelNormalizedX);
    const modelY = Number((f as any)._modelNormalizedY);

    if (meta && Number.isFinite(modelX) && Number.isFinite(modelY)) {
      const safeScale = Math.max(1e-6, Number(meta.scale) || 1);
      const realX = (modelX / 1000) * Number(meta.base64Width || 0) - Number(meta.offsetX || 0);
      const realY = (modelY / 1000) * Number(meta.base64Height || 0) - Number(meta.offsetY || 0);
      const mappedX = realX / safeScale;
      const mappedY = realY / safeScale;
      if (Number.isFinite(mappedX) && Number.isFinite(mappedY)) {
        return {
          x: Math.max(0, Math.min(ratio.naturalWidth, mappedX)),
          y: Math.max(0, Math.min(ratio.naturalHeight, mappedY)),
        };
      }
    }

    return {
      x: Math.max(0, Math.min(ratio.naturalWidth, fallbackX)),
      y: Math.max(0, Math.min(ratio.naturalHeight, fallbackY)),
    };
  };

  const getModelMappedOriginalPixel = (f: Fixture): ManualDebugPoint | null => {
    const ratio = getImageDisplayRatio();
    const meta = (f as any)._analysisCoordMeta as
      | { base64Width: number; base64Height: number; offsetX: number; offsetY: number; scale: number }
      | undefined;
    const modelX = Number((f as any)._modelNormalizedX);
    const modelY = Number((f as any)._modelNormalizedY);
    if (!meta || !Number.isFinite(modelX) || !Number.isFinite(modelY)) return null;
    const safeScale = Math.max(1e-6, Number(meta.scale) || 1);
    const realX = (modelX / 1000) * Number(meta.base64Width || 0) - Number(meta.offsetX || 0);
    const realY = (modelY / 1000) * Number(meta.base64Height || 0) - Number(meta.offsetY || 0);
    const mappedX = realX / safeScale;
    const mappedY = realY / safeScale;
    if (!Number.isFinite(mappedX) || !Number.isFinite(mappedY)) return null;
    return {
      x: Math.max(0, Math.min(ratio.naturalWidth, mappedX)),
      y: Math.max(0, Math.min(ratio.naturalHeight, mappedY)),
    };
  };

  const getCurrentFixtureDisplayPos = (f: Fixture) => {
    const { x: naturalX, y: naturalY } = getFixtureOriginalPixel(f);
    return toDisplayPosFromOriginalPixel(naturalX, naturalY);
  };

  const getManualEmissionMode = (f: Fixture): '' | 'up' | 'down' | 'up_down' => {
    const raw = String((f as any).emissionMode || '').toLowerCase();
    if (raw === 'up' || raw === 'down' || raw === 'up_down') return raw;
    const label = String(f.label || '').toLowerCase();
    const hasUp = label.includes('上照') || label.includes('up');
    const hasDown = label.includes('下照') || label.includes('down');
    if (hasUp && hasDown) return 'up_down';
    if (hasUp) return 'up';
    if (hasDown) return 'down';
    return '';
  };

  const getManualConstraintAnchors = (f: Fixture, naturalWidth: number, naturalHeight: number): ManualDebugPoint[] => {
    const shortEdge = Math.max(1, Math.min(naturalWidth, naturalHeight));
    const base = getFixtureOriginalPixel(f);
    const clampPoint = (p: ManualDebugPoint): ManualDebugPoint => ({
      x: Math.max(0, Math.min(naturalWidth, p.x)),
      y: Math.max(0, Math.min(naturalHeight, p.y)),
    });
    const t = String(f.type || '').toLowerCase();
    const mode = getManualEmissionMode(f);
    const emitUp = mode === 'up' || mode === 'up_down' || mode === '';
    const emitDown = mode === 'down' || mode === 'up_down' || mode === '';

    if (t === 'spotlight' || t === 'downlight' || t === 'cove_light' || t === 'cove_lighting') {
      return [clampPoint(base)];
    }
    if (t === 'wall_lamp') {
      const out: ManualDebugPoint[] = [];
      if (emitUp) out.push(clampPoint({ x: base.x, y: base.y - shortEdge * 0.035 }));
      if (emitDown) out.push(clampPoint({ x: base.x, y: base.y + shortEdge * 0.045 }));
      if (out.length === 0) out.push(clampPoint(base));
      return out;
    }
    if (t === 'floor_lamp' || t === 'table_lamp') {
      const out: ManualDebugPoint[] = [];
      if (emitUp) out.push(clampPoint({ x: base.x, y: base.y - shortEdge * 0.08 }));
      if (emitDown) out.push(clampPoint({ x: base.x, y: base.y + shortEdge * 0.09 }));
      if (out.length === 0) out.push(clampPoint(base));
      return out;
    }
    return [clampPoint(base)];
  };

  /** B 点：筒灯/射灯沿 rotation 前移一段，避免与 A 完全重合，便于对照结果漂移 */
  const getManualDebugBPoints = (f: Fixture, naturalWidth: number, naturalHeight: number): ManualDebugPoint[] => {
    const clampPoint = (p: ManualDebugPoint): ManualDebugPoint => ({
      x: Math.max(0, Math.min(naturalWidth, p.x)),
      y: Math.max(0, Math.min(naturalHeight, p.y)),
    });
    const t = String(f.type || '').toLowerCase();
    const base = getFixtureOriginalPixel(f);
    if (t === 'spotlight' || t === 'downlight') {
      const rot = ((Number(f.rotation) || 90) * Math.PI) / 180;
      const shortEdge = Math.max(1, Math.min(naturalWidth, naturalHeight));
      const dist = Math.max(24, shortEdge * 0.14);
      return [
        clampPoint({
          x: base.x + Math.cos(rot) * dist,
          y: base.y + Math.sin(rot) * dist,
        }),
      ];
    }
    return getManualConstraintAnchors(f, naturalWidth, naturalHeight);
  };

  useEffect(() => {
    if (!manualDebugEnabled || !originalImage) {
      setManualDebugEntries([]);
      return;
    }
    const manualCandidates = fixtures.filter((f) => {
      const source = String((f as any).source || 'auto').toLowerCase();
      return source === 'manual' && MANUAL_ADD_LIGHT_TYPES.has(String(f.type || '').toLowerCase());
    });
    const selectedManualCandidates = manualCandidates.filter(
      (f) => selectedFixtureIds.includes(f.id) && !!f.isModified
    );
    const targets = selectedManualCandidates.length > 0
      ? selectedManualCandidates
      : manualCandidates.filter((f) => !!f.isModified);
    if (targets.length === 0) {
      setManualDebugEntries([]);
      return;
    }

    if (!renderedImage) {
      const ratio = getImageDisplayRatio();
      const w = ratio.naturalWidth;
      const h = ratio.naturalHeight;
      const entries: ManualDebugEntry[] = targets.map((f) => ({
        id: f.id,
        a: getFixtureOriginalPixel(f),
        b: getManualDebugBPoints(f, w, h),
        c: [],
      }));
      setManualDebugEntries(entries);
      return;
    }

    let cancelled = false;
    const run = async () => {
      const loadImage = (src: string) =>
        new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = () => reject(new Error('image load failed'));
          img.src = src;
        });
      try {
        const [origImg, rendImg] = await Promise.all([loadImage(originalImage), loadImage(renderedImage)]);
        if (cancelled) return;
        const w = Math.max(1, Math.min(origImg.naturalWidth || origImg.width, rendImg.naturalWidth || rendImg.width));
        const h = Math.max(1, Math.min(origImg.naturalHeight || origImg.height, rendImg.naturalHeight || rendImg.height));
        const c0 = document.createElement('canvas');
        const c1 = document.createElement('canvas');
        c0.width = w; c0.height = h;
        c1.width = w; c1.height = h;
        const g0 = c0.getContext('2d');
        const g1 = c1.getContext('2d');
        if (!g0 || !g1) return;
        g0.drawImage(origImg, 0, 0, w, h);
        g1.drawImage(rendImg, 0, 0, w, h);
        const d0 = g0.getImageData(0, 0, w, h).data;
        const d1 = g1.getImageData(0, 0, w, h).data;
        const shortEdge = Math.max(1, Math.min(w, h));

        const centroidInDisk = (
          anchor: ManualDebugPoint,
          radiusPx: number,
          clampRef: ManualDebugPoint
        ): ManualDebugPoint => {
          const cx = Math.round(anchor.x);
          const cy = Math.round(anchor.y);
          const r = Math.max(10, Math.round(radiusPx));
          const x0 = Math.max(0, cx - r);
          const y0 = Math.max(0, cy - r);
          const x1 = Math.min(w - 1, cx + r);
          const y1 = Math.min(h - 1, cy + r);
          let sumW = 0;
          let sumX = 0;
          let sumY = 0;
          let bestW = 0;
          let bestX = cx;
          let bestY = cy;
          for (let y = y0; y <= y1; y++) {
            for (let x = x0; x <= x1; x++) {
              const dx = x - cx;
              const dy = y - cy;
              if (dx * dx + dy * dy > r * r) continue;
              const i = (y * w + x) * 4;
              const l0 = 0.2126 * d0[i] + 0.7152 * d0[i + 1] + 0.0722 * d0[i + 2];
              const l1v = 0.2126 * d1[i] + 0.7152 * d1[i + 1] + 0.0722 * d1[i + 2];
              const delta = Math.max(0, l1v - l0 - 4);
              if (delta <= 0) continue;
              const wgt = Math.pow(delta, 1.2);
              sumW += wgt;
              sumX += x * wgt;
              sumY += y * wgt;
              if (wgt > bestW) {
                bestW = wgt;
                bestX = x;
                bestY = y;
              }
            }
          }
          const raw = sumW > 1e-3 ? { x: sumX / sumW, y: sumY / sumW } : { x: bestX, y: bestY };
          const rdx = raw.x - clampRef.x;
          const rdy = raw.y - clampRef.y;
          const dist = Math.sqrt(rdx * rdx + rdy * rdy);
          const maxShift = Math.max(12, r * 0.55);
          if (dist > maxShift && dist > 1e-6) {
            const k = maxShift / dist;
            return { x: clampRef.x + rdx * k, y: clampRef.y + rdy * k };
          }
          return raw;
        };

        const entries: ManualDebugEntry[] = targets.map((f) => {
          const a = getFixtureOriginalPixel(f);
          const b = getManualDebugBPoints(f, w, h);
          const t = String(f.type || '').toLowerCase();
          const isCeilingDirectional = t === 'spotlight' || t === 'downlight';
          let c: ManualDebugPoint[];
          if (isCeilingDirectional) {
            const beamLen = Math.hypot(b[0].x - a.x, b[0].y - a.y);
            const searchR = Math.max(shortEdge * 0.3, beamLen * 3.2);
            c = [centroidInDisk(a, searchR, a)];
          } else {
            const radius =
              t === 'wall_lamp'
                ? shortEdge * 0.12
                : t === 'floor_lamp' || t === 'table_lamp'
                  ? shortEdge * 0.14
                  : shortEdge * 0.24;
            c = b.map((anchor) => centroidInDisk(anchor, radius, anchor));
          }
          return { id: f.id, a, b, c };
        });

        if (!cancelled) setManualDebugEntries(entries);
      } catch {
        if (!cancelled) setManualDebugEntries([]);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [manualDebugEnabled, originalImage, renderedImage, fixtures, selectedFixtureIds]);

  useEffect(() => {
    if (!autoCoveDebugEnabled || !originalImage) {
      setAutoCoveDebugEntries([]);
      return;
    }
    const autoCoveCandidates = fixtures.filter((f) => {
      const source = String((f as any).source || 'auto').toLowerCase();
      const type = String(f.type || '').toLowerCase();
      return source === 'auto' && (type === 'cove_light' || type === 'cove_lighting');
    });
    const selectedAutoCove = autoCoveCandidates.filter((f) => selectedFixtureIds.includes(f.id));
    const targets = selectedAutoCove.length > 0
      ? selectedAutoCove
      : (autoCoveCandidates.length > 0 ? [autoCoveCandidates[autoCoveCandidates.length - 1]] : []);
    if (targets.length === 0) {
      setAutoCoveDebugEntries([]);
      return;
    }

    let cancelled = false;
    const run = async () => {
      const loadImage = (src: string) =>
        new Promise<HTMLImageElement>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = () => reject(new Error('image load failed'));
          img.src = src;
        });
      try {
        const img = await loadImage(originalImage);
        if (cancelled) return;
        const w = Math.max(1, img.naturalWidth || img.width);
        const h = Math.max(1, img.naturalHeight || img.height);
        const c = document.createElement('canvas');
        c.width = w;
        c.height = h;
        const g = c.getContext('2d');
        if (!g) return;
        g.drawImage(img, 0, 0, w, h);
        const data = g.getImageData(0, 0, w, h).data;
        const shortEdge = Math.max(1, Math.min(w, h));

        const entries: AutoCoveDebugEntry[] = targets.map((f) => {
          const a = getModelMappedOriginalPixel(f) || getFixtureOriginalPixel(f);
          const b0 = getFixtureOriginalPixel(f);
          const b = [{ x: b0.x, y: b0.y }];
          const r = Math.max(14, Math.round(shortEdge * 0.08));
          const cx = Math.round(b0.x);
          const cy = Math.round(b0.y);
          const x0 = Math.max(0, cx - r);
          const y0 = Math.max(0, cy - r);
          const x1 = Math.min(w - 1, cx + r);
          const y1 = Math.min(h - 1, cy + r);
          let maxL = 0;
          for (let y = y0; y <= y1; y++) {
            for (let x = x0; x <= x1; x++) {
              const dx = x - cx;
              const dy = y - cy;
              if (dx * dx + dy * dy > r * r) continue;
              const i = (y * w + x) * 4;
              const l = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
              if (l > maxL) maxL = l;
            }
          }
          const threshold = Math.max(18, maxL * 0.82);
          let sumW = 0;
          let sumX = 0;
          let sumY = 0;
          for (let y = y0; y <= y1; y++) {
            for (let x = x0; x <= x1; x++) {
              const dx = x - cx;
              const dy = y - cy;
              const dist = Math.sqrt(dx * dx + dy * dy);
              if (dist > r) continue;
              const i = (y * w + x) * 4;
              const l = 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
              if (l < threshold) continue;
              const centerBias = 1 / (1 + dist * 0.2);
              const wgt = Math.pow(l - threshold + 1, 1.25) * centerBias;
              sumW += wgt;
              sumX += x * wgt;
              sumY += y * wgt;
            }
          }
          let cPoint = sumW > 1e-6 ? { x: sumX / sumW, y: sumY / sumW } : { x: b0.x, y: b0.y };
          const dx = cPoint.x - b0.x;
          const dy = cPoint.y - b0.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const maxShift = Math.max(12, r * 0.45);
          if (dist > maxShift && dist > 1e-6) {
            const k = maxShift / dist;
            cPoint = { x: b0.x + dx * k, y: b0.y + dy * k };
          }
          return { id: f.id, a, b, c: [cPoint] };
        });

        if (!cancelled) setAutoCoveDebugEntries(entries);
      } catch {
        if (!cancelled) setAutoCoveDebugEntries([]);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [autoCoveDebugEnabled, originalImage, fixtures, selectedFixtureIds]);

  if (!originalImage) {
    return (
      <div 
        className={`flex-1 h-full relative flex flex-col items-center justify-center gap-6 overflow-hidden transition-colors duration-300 ${isDarkMode ? 'bg-black border-r border-white/5' : 'bg-zinc-50 border-r border-zinc-200'}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* 背景网格 */}
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: isDarkMode
              ? 'linear-gradient(#222 1px, transparent 1px), linear-gradient(90deg, #222 1px, transparent 1px)'
              : 'linear-gradient(#d4d4d4 1px, transparent 1px), linear-gradient(90deg, #d4d4d4 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />

        {/* 拖拽时的遮罩层 */}
        {isDraggingFile && (
          <div className="absolute inset-0 z-20 bg-[#D7CDA1]/20 backdrop-blur-sm flex items-center justify-center transition-all">
            <div className={`w-[600px] max-w-[90vw] h-[400px] max-h-[50vh] rounded-3xl flex flex-col items-center justify-center border-2 border-dashed border-[#D7CDA1] ${isDarkMode ? 'bg-neutral-900/80' : 'bg-white/80'}`}>
              <svg className="w-16 h-16 text-[#D7CDA1] mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2-2v12a2 2 0 002 2z" />
              </svg>
              <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                {language !== 'zh' ? 'Drop image here' : '拖入图片到此处'}
              </p>
            </div>
          </div>
        )}

        <label 
          htmlFor="img-upload" 
          className={`relative z-10 w-[600px] max-w-[90vw] h-[400px] max-h-[50vh] rounded-3xl flex flex-col items-center justify-center group cursor-pointer border border-solid hover:border-[#D7CDA1] transition-all ${isDarkMode ? 'bg-neutral-900/20 border-neutral-700' : 'bg-white border-zinc-300'} ${isDraggingFile ? 'opacity-50' : ''}`}
        >
          <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 border group-hover:border-[#D7CDA1]/30 ${isDarkMode ? 'bg-[#111] border-neutral-800' : 'bg-zinc-100 border-zinc-300'}`}>
            <svg className="w-10 h-10 text-[#D7CDA1]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2-2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div className="text-center space-y-2 w-full">
            <h2 className={`text-3xl font-black tracking-wide mb-3 whitespace-nowrap ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
              {tK('click_to_load_image')}
            </h2>
            <p
              className={`sidebar-btn-official-font sidebar-btn-label sidebar-btn-label-95 uppercase whitespace-nowrap ${
                isDarkMode ? 'text-neutral-500' : 'text-zinc-600'
              }`}
            >
              {tK('high_resolution_ready')}
            </p>
            <p className={`text-sm mt-2 ${isDarkMode ? 'text-neutral-400' : 'text-zinc-500'}`}>
              {language !== 'zh' ? 'or drag and drop image here' : '或将图片拖放到此处'}
            </p>
          </div>
        </label>

      </div>
    );
  }

  const commonImgClasses = "block max-w-full max-h-[92vh] object-contain transition-all duration-300";

  return (
    <div
      className={`flex-1 flex flex-col items-center justify-center border-r relative pb-28 transition-colors duration-300 ${isDarkMode ? 'border-white/5' : 'border-zinc-200'}`}
    >
      <div 
        ref={containerRef} 
        className={`relative max-w-[95%] sm:max-w-[96%] max-h-[85vh] sm:max-h-[90vh] md:max-h-[92vh] rounded-sm select-none ${isComparing ? 'touch-none' : ''}`}
        style={{ overflow: isComparing ? 'visible' : 'hidden' }}
      >
        {isComparing && originalImage ? (
          <div 
            ref={imageContainerRef}
            className="relative w-full flex items-center justify-center touch-none"
            style={{ 
              overflow: 'visible',
              minHeight: '400px', // ȷС߶
              height: 'auto'
            }}
          >
            <div 
              className="relative inline-block touch-none"
              style={{ 
                transform: `scale(${zoomLevel}) translate(${panX / zoomLevel}px, ${panY / zoomLevel}px)`, 
                transformOrigin: 'center center',
                transition: isDragging || isPanning ? 'none' : 'transform 0.1s ease-out',
                maxWidth: '100%',
                maxHeight: '92vh'
              }}
            >
              {/* ͳһͼƬ - ο2.txtͼƬȫغ */}
              <div 
                ref={imageWrapperRef}
                className={`relative ${isEditMode ? 'cursor-crosshair' : ''}`}
                onClick={handleImageClick}
                style={{ 
                  position: 'relative',
                  overflow: 'hidden',
                  display: 'inline-block',
                  lineHeight: 0,
                  minWidth: '200px',
                  minHeight: '200px'
                }}
              >
                {/* ײ㣺ԭʼͼ - ʹ relative λԶӦͼƬߴ */}
                <img 
                  ref={originalImageRef}
                  src={originalImage} 
                  alt="Original Base"
                  className="select-none pointer-events-none"
                  style={{
                    position: 'relative',
                    display: 'block',
                    maxWidth: 'calc(100vw - 40px)',
                    maxHeight: 'calc(100vh - 100px)',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'cover',

                    objectPosition: 'center',
                    margin: 0,
                    padding: 0,
                    border: 'none',
                    outline: 'none'
                  }}
                  onLoad={(e) => {
                    // ԭʼͼɺ󣬸ͼƬ߽ͬͼߴ
                    const img = e.currentTarget;
                    if (originalImageRef.current && imageWrapperRef.current && containerRef.current) {
                      const wrapper = imageWrapperRef.current;
                      
                      // ԶӦͼƬߴ磨ΪͼƬ relative λ
                      // ͬͼߴ磨Ѽأ- ؼȷͼխС
                      if (renderedImageRef.current) {
                        const rendered = renderedImageRef.current;
                        // ̶Ϊȣο2.txtʵ֣
                        rendered.style.width = `${img.offsetWidth}px`;
                        rendered.style.height = `${img.offsetHeight}px`;
                        rendered.style.maxWidth = 'none';
                      }
                      
                      // ͼƬʵʾڻ˶λ
                      requestAnimationFrame(() => {
                        if (originalImageRef.current && containerRef.current) {
                          const imageRect = originalImageRef.current.getBoundingClientRect();
                          const containerRect = containerRef.current.getBoundingClientRect();
                          setImageBounds({
                            left: imageRect.left - containerRect.left,
                            top: imageRect.top - containerRect.top,
                            width: imageRect.width,
                            height: imageRect.height,
                          });
                        }
                      });
                    }
                  }}
                />
                
                {/* ֲ㣺ͼͨı width ʵֲüЧ - ο2.txt */}
                {renderedImage && (
                  <div
                    className="absolute top-0 left-0 h-full overflow-hidden z-10"
                    style={{
                      width: `${comparisonPos}%`,
                      transition: isDragging ? 'none' : 'width 0.1s ease-out',
                      borderRight: '2px solid white'
                    }}
                  >
                    {/* ϲ㣺ͼ - ʹ absolute λȫصԭʼͼ */}
                    <img 
                      ref={renderedImageRef}
                      src={renderedImage} 
                      alt="Rendered Overlay"
                      className="select-none pointer-events-none"
                      style={{
                        position: 'absolute',
                        top: '0',
                        left: '0',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',

                        objectPosition: 'center',
                        display: 'block',
                        margin: '0',
                        padding: '0',
                        border: 'none',
                        outline: 'none',
                        maxWidth: 'none' // ?? ؼȷͼƬֲխС
                      }}
                      onLoad={(e) => {
                        // ͼɺͬߴ - ο2.txtĹؼ
                        const rendered = e.currentTarget;
                        if (originalImageRef.current && imageWrapperRef.current) {
                          const original = originalImageRef.current;
                          
                          // ?? ؼȷͼխСο2.txt117У
                          // ʹԭʼͼĳߴ磬ȷȫ
                          rendered.style.width = `${original.offsetWidth}px`;
                          rendered.style.height = `${original.offsetHeight}px`;
                          rendered.style.maxWidth = 'none';
                        }
                      }}
                    />
                  </div>
                )}
                {/* 水印叠整张画布右下角：勿放在上方剪裁层内，否则对比滑杆偏左时只剩「鱼+NEMO」线稿，正文被裁掉 */}
                {renderedImage && (
                  <div className="absolute bottom-2 right-2 z-[20] pointer-events-none">
                    <Watermark size="small" />
                  </div>
                )}
              </div>

            </div>
            
            {/* Slider Handle：加宽命中区 + Pointer Events（触控 / Apple Pencil） */}
            <div
              className="absolute top-0 bottom-0 z-30 flex justify-center cursor-ew-resize touch-none group slider-handle"
              style={{
                left: imageBounds ? `${imageBounds.left + (comparisonPos / 100) * imageBounds.width}px` : `${comparisonPos}%`,
                top: imageBounds ? `${imageBounds.top}px` : 0,
                height: imageBounds ? `${imageBounds.height}px` : '100%',
                width: 44,
                marginLeft: -22,
                transition: isDragging ? 'none' : 'left 0.1s ease-out',
              }}
              onPointerDown={(e) =>
                startEditorCompareSliderPointerDrag(e, {
                  originalImageRef,
                  containerRef,
                  setSliderPercent: setComparisonPos,
                  setDragging: setIsDragging,
                  setImageBounds,
                })
              }
              onTouchStart={(e) =>
                startEditorCompareSliderTouchDrag(e, {
                  originalImageRef,
                  containerRef,
                  setSliderPercent: setComparisonPos,
                  setDragging: setIsDragging,
                  setImageBounds,
                })
              }
            >
              <div className="w-0.5 h-full shrink-0 bg-white self-center pointer-events-none" />
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-2xl flex items-center justify-center text-[#101820] group-hover:scale-110 transition-transform select-none pointer-events-none"
                aria-hidden
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="opacity-80">
                  <path d="M8 6l-6 6 6 6" />
                  <path d="M16 6l6 6-6 6" />
                </svg>
              </div>
            </div>
          </div>
        ) : (
          <div 
            ref={imageWrapperRef}
            className={`relative inline-block ${isEditMode ? 'cursor-crosshair' : ''}`}
            onClick={handleImageClick}
            style={{ overflow: 'visible' }}
          >
            <img 
              ref={originalImageRef}
              src={showOriginal ? originalImage : (renderedImage || originalImage)} 
              alt="Target"
              className={`${commonImgClasses} ${renderedImage ? '' : 'brightness-[0.6] saturate-[0.8]'}`}
              style={{ 
                pointerEvents: isEditMode ? 'none' : 'auto',
              }} // ?? ༭ģʽ½ͼƬĵ¼յ
              onLoad={() => {
                if (originalImageRef.current && containerRef.current) {
                  const imageRect = originalImageRef.current.getBoundingClientRect();
                  const containerRect = containerRef.current.getBoundingClientRect();
                  setImageBounds({
                    left: imageRect.left - containerRect.left,
                    top: imageRect.top - containerRect.top,
                    width: imageRect.width,
                    height: imageRect.height,
                  });
                }
              }}
            />
            {/* ˮӡ - ֻȾͼʾ */}
            {renderedImage && (
              <div className="absolute bottom-2 right-2 z-[15] pointer-events-none">
                <Watermark size="small" />
              </div>
            )}
          </div>
        )}

        {/* Interaction Layer - 原图视图下显示识别光点 */}
        {!isComparing && showOriginal && fixtures.map(f => renderLightStyles(f))}
        {!isComparing && showOriginal && fixtures.map((f) => {
          const isSelected = selectedFixtureIds.includes(f.id);
          const isModified = f.isModified;
          const isOff = f.intensity === 0;
          
          // ?? жϹԴԴAIʶsourceΪ'auto'undefinedֶӣsourceΪ'manual'
          const source = (f as any).source || 'auto'; // ĬΪAIʶ
          const isAIDetected = source === 'auto' || source === undefined;
          const isManualAdded = source === 'manual';
          
          // ?? ж״̬AIʶĬֶӵĬδͨ˫л
          const isLocked = (f as any).locked !== undefined ? (f as any).locked : isAIDetected;
          
          // ?? ݹԴȷֶӵɫ
          const getManualPointColor = (type: string) => {
            // 1-2еͣǳɫף ɫ
            const lightYellowTypes = ['pendant', 'downlight', 'spotlight', 'wall_lamp', 'floor_lamp', 'table_lamp'];
            // 3еͣɫף ɫ
            const lightBlueTypes = ['cove_light', 'cove_lighting'];
            // 4еͣɫף ɫ
            const lightGreenTypes = ['window', 'window_light', 'sunlight', 'natural_light', 'ambient_light', 'other'];
            
            if (lightYellowTypes.includes(type)) {
              return '#D7CDA1'; // ɫ
            } else if (lightBlueTypes.includes(type)) {
              return '#4A90E2'; // ɫ
            } else if (lightGreenTypes.includes(type)) {
              return '#52C41A'; // ɫ
            }
            return '#D7CDA1'; // Ĭɫ
          };
          
          // ?? ȷɫ
          let pointColor = '#facc15'; // ĬϻɫAIʶ㣩
          let pointBorderColor = 'rgba(255, 255, 255, 0.6)';
          let pointShadow = 'none';
          
          if (isOff) {
            pointColor = '#000000';
            pointBorderColor = 'rgba(255, 255, 255, 0.4)';
            pointShadow = 'none';
          } else if (isSelected) {
            pointColor = '#22c55e';
            pointBorderColor = 'rgba(255, 255, 255, 0.9)';
            pointShadow = 'none';
          } else if (isManualAdded) {
            // ֶӵĵ㣺ʾӦɫ
            pointColor = getManualPointColor(f.type);
            pointBorderColor = 'rgba(255, 255, 255, 0.8)';
            // ɫתΪrgbaʽӰ
            const hexToRgba = (hex: string, alpha: number) => {
              const r = parseInt(hex.slice(1, 3), 16);
              const g = parseInt(hex.slice(3, 5), 16);
              const b = parseInt(hex.slice(5, 7), 16);
              return `rgba(${r},${g},${b},${alpha})`;
            };
            pointShadow = `0 0 15px ${hexToRgba(pointColor, 0.6)}`;
          } else if (isAIDetected) {
            // AIʶ㣺лɫ
            pointColor = '#facc15';
            pointBorderColor = 'rgba(255, 255, 255, 0.6)';
            pointShadow = '0 0 15px rgba(250,204,21,0.9)';
          }
          
          const currentDisplayPos = getCurrentFixtureDisplayPos(f);
          return (
            <div 
              key={f.id}
              className={`absolute z-[120] cursor-pointer group transition-all duration-300 ${isSelected && !isOff ? 'scale-110' : !isSelected ? 'hover:scale-110' : ''}`}
              style={{ 
                pointerEvents: 'auto', // ?? ȷԴԽյ¼
                left: `${currentDisplayPos.left.toFixed(3)}px`,
                top: `${currentDisplayPos.top.toFixed(3)}px`,
                transform: 'translate(-50%, -50%)' 
              }}
              onClick={(e) => {
                // קѡ
                if (draggingFixtureId === f.id) {
                  return;
                }
                onSelectFixture(f.id, {
                  additive: e.shiftKey || e.metaKey,
                  range: e.ctrlKey && !e.shiftKey,
                });
              }}
              onDoubleClick={(e) => handleFixtureDoubleClick(e, f.id)}
              onMouseDown={(e) => handleFixtureMouseDown(e, f.id, isLocked, isManualAdded)}
            >
              <div 
                className={`
                  w-4 h-4 rounded-full border-2 transition-all relative
                  ${isOff ? 'opacity-40' : ''}
                  ${!isOff && isSelected ? 'fixture-point-selected-breathe' : ''}
                  ${!isOff && !isSelected && isAIDetected ? 'animate-pulse' : ''}
                  ${isEditMode && !isOff ? 'cursor-move' : 'cursor-pointer'}
                `}
                style={{
                  backgroundColor: pointColor,
                  borderColor: pointBorderColor,
                  boxShadow: undefined,
                }}
              >
                {/* ?? ͼ꣺ʾ״̬AIʶĬֶӵĬδͨ˫л */}
                {isLocked && !isOff && (
                  <svg 
                    className="absolute -top-1 -right-1 w-3 h-3 text-white opacity-90 z-10"
                    fill="currentColor" 
                    viewBox="0 0 20 20"
                    style={{ filter: 'none' }}
                  >
                    <title>{tK('locked_double_click')}</title>
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                )}
                
                {/* ?? δʾ༭ģʽʾͼ */}
                {!isLocked && !isOff && isEditMode && (
                  <svg 
                    className="absolute -top-1 -right-1 w-3 h-3 text-white opacity-60 z-10"
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 20 20"
                    style={{ filter: 'none' }}
                  >
                    <title>{tK('unlocked_double_click')}</title>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                  </svg>
                )}
                
                {/* ?? ֶ΢ĵ㣺ǣȦߣ */}
                {isModified && !isOff && (
                  <div 
                    className="absolute inset-0 rounded-full border-2 border-dashed pointer-events-none"
                    style={{
                      borderColor: isSelected ? '#4ade80' : isAIDetected ? '#facc15' : pointColor,
                      transform: 'scale(1.4)',
                      opacity: 0.7,
                    }}
                  />
                )}
              </div>

            </div>
          );
        })}

        {/* A/B/C 调试层：仅“增加光源8种灯”，默认关闭（window.__NEMO_MANUAL_LIGHT_DEBUG_8 = true） */}
        {!isComparing && manualDebugEnabled && manualDebugEntries
          .filter((entry, i, arr) => {
            if (selectedFixtureIds.length > 0) {
              return selectedFixtureIds.includes(entry.id);
            }
            // 未选中任何点时，只显示最后一个手动新增光点的调试结果，避免满屏点位干扰
            return i === arr.length - 1;
          })
          .map((entry, idx) => {
          const o = manualDebugOverrides[entry.id];
          const merged: ManualDebugEntry = o
            ? { id: entry.id, a: o.a ?? entry.a, b: o.b ?? entry.b, c: o.c ?? entry.c }
            : entry;
          const aPos = toDisplayPosFromOriginalPixel(merged.a.x, merged.a.y);
          return (
            <React.Fragment key={`manual-debug-${entry.id}`}>
              <div
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setDraggingFixtureId(null);
                  if (!onMoveFixture) return;
                  debugDragPointerSeedRef.current = { x: e.clientX, y: e.clientY };
                  setDebugPointDrag({ kind: 'manual', fixtureId: entry.id, role: 'a' });
                }}
                style={{
                  position: 'absolute',
                  left: `${aPos.left.toFixed(3)}px`,
                  top: `${aPos.top.toFixed(3)}px`,
                  width: '22px',
                  height: '22px',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 220,
                  cursor: onMoveFixture ? 'move' : 'default',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title={`A${idx + 1}: 点击点（可拖动，等同移动灯具）`}
              >
                <div
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: '#ef4444',
                    boxShadow: 'none',
                    pointerEvents: 'none',
                  }}
                />
              </div>
              {merged.b.map((bp, bi) => {
                const bPos = toDisplayPosFromOriginalPixel(bp.x, bp.y);
                return (
                  <div
                    key={`b-${entry.id}-${bi}`}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setDraggingFixtureId(null);
                      debugDragPointerSeedRef.current = { x: e.clientX, y: e.clientY };
                      setDebugPointDrag({ kind: 'manual', fixtureId: entry.id, role: 'b', bIndex: bi });
                    }}
                    style={{
                      position: 'absolute',
                      left: `${bPos.left.toFixed(3)}px`,
                      top: `${bPos.top.toFixed(3)}px`,
                      width: '22px',
                      height: '22px',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 221,
                      cursor: 'move',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title={`B${idx + 1}-${bi + 1}: 约束目标（可拖动）`}
                  >
                    <div
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#22c55e',
                        boxShadow: 'none',
                        pointerEvents: 'none',
                      }}
                    />
                  </div>
                );
              })}
              {merged.c.map((cp, ci) => {
                const cPos = toDisplayPosFromOriginalPixel(cp.x, cp.y);
                return (
                  <div
                    key={`c-${entry.id}-${ci}`}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setDraggingFixtureId(null);
                      debugDragPointerSeedRef.current = { x: e.clientX, y: e.clientY };
                      setDebugPointDrag({ kind: 'manual', fixtureId: entry.id, role: 'c', cIndex: ci });
                    }}
                    style={{
                      position: 'absolute',
                      left: `${cPos.left.toFixed(3)}px`,
                      top: `${cPos.top.toFixed(3)}px`,
                      width: '22px',
                      height: '22px',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 222,
                      cursor: 'move',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title={`C${idx + 1}-${ci + 1}: 结果增量质心（可拖动）`}
                  >
                    <div
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#06b6d4',
                        boxShadow: 'none',
                        pointerEvents: 'none',
                      }}
                    />
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}

        {!isComparing && showOriginal && autoCoveDebugEnabled && autoCoveDebugEntries.map((entry, idx) => {
          const ao = autoCoveDebugOverrides[entry.id];
          const merged: AutoCoveDebugEntry = ao
            ? { id: entry.id, a: ao.a ?? entry.a, b: ao.b ?? entry.b, c: ao.c ?? entry.c }
            : entry;
          const aPos = toDisplayPosFromOriginalPixel(merged.a.x, merged.a.y);
          return (
            <React.Fragment key={`auto-cove-debug-${entry.id}`}>
              <div
                onMouseDown={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setDraggingFixtureId(null);
                  debugDragPointerSeedRef.current = { x: e.clientX, y: e.clientY };
                  setDebugPointDrag({ kind: 'autoCove', fixtureId: entry.id, role: 'a' });
                }}
                style={{
                  position: 'absolute',
                  left: `${aPos.left.toFixed(3)}px`,
                  top: `${aPos.top.toFixed(3)}px`,
                  width: '24px',
                  height: '24px',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 223,
                  cursor: 'move',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title={`A${idx + 1}: 模型原始映射点（可拖动，仅显示）`}
              >
                <div
                  style={{
                    width: '9px',
                    height: '9px',
                    borderRadius: '50%',
                    background: '#ef4444',
                    boxShadow: 'none',
                    pointerEvents: 'none',
                  }}
                />
              </div>
              {merged.b.map((bp, bi) => {
                const bPos = toDisplayPosFromOriginalPixel(bp.x, bp.y);
                return (
                  <div
                    key={`auto-b-${entry.id}-${bi}`}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setDraggingFixtureId(null);
                      if (!onMoveFixture) return;
                      debugDragPointerSeedRef.current = { x: e.clientX, y: e.clientY };
                      setDebugPointDrag({ kind: 'autoCove', fixtureId: entry.id, role: 'b', bIndex: bi });
                    }}
                    style={{
                      position: 'absolute',
                      left: `${bPos.left.toFixed(3)}px`,
                      top: `${bPos.top.toFixed(3)}px`,
                      width: '24px',
                      height: '24px',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 224,
                      cursor: onMoveFixture ? 'move' : 'default',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title={`B${idx + 1}-${bi + 1}: 当前校正点（可拖动，等同移动灯具；锁定灯无效）`}
                  >
                    <div
                      style={{
                        width: '9px',
                        height: '9px',
                        borderRadius: '50%',
                        background: '#22c55e',
                        boxShadow: 'none',
                        pointerEvents: 'none',
                      }}
                    />
                  </div>
                );
              })}
              {merged.c.map((cp, ci) => {
                const cPos = toDisplayPosFromOriginalPixel(cp.x, cp.y);
                return (
                  <div
                    key={`auto-c-${entry.id}-${ci}`}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setDraggingFixtureId(null);
                      debugDragPointerSeedRef.current = { x: e.clientX, y: e.clientY };
                      setDebugPointDrag({ kind: 'autoCove', fixtureId: entry.id, role: 'c', cIndex: ci });
                    }}
                    style={{
                      position: 'absolute',
                      left: `${cPos.left.toFixed(3)}px`,
                      top: `${cPos.top.toFixed(3)}px`,
                      width: '24px',
                      height: '24px',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 225,
                      cursor: 'move',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title={`C${idx + 1}-${ci + 1}: 建议校正点（可拖动，仅显示）`}
                  >
                    <div
                      style={{
                        width: '9px',
                        height: '9px',
                        borderRadius: '50%',
                        background: '#06b6d4',
                        boxShadow: 'none',
                        pointerEvents: 'none',
                      }}
                    />
                  </div>
                );
              })}
            </React.Fragment>
          );
        })}

        {/* 已按需求关闭射灯控制点/角度交互层 */}

        {/* Bottom Control Panel */}
        <div 
        className={`absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-0 overflow-hidden transition-all duration-500 z-30 min-w-[320px]`}
          style={{
            borderRadius: '6px', // rounded-3xl (24px) 20%ټ10% = 17.1px  17px
            // ǳɫģʽɫģʽͬ͸
            background: isDarkMode ? 'rgba(20, 20, 25, 0.0875)' : 'rgba(255, 255, 255, 0.0875)', // ְ͸Ч
            backdropFilter: 'blur(14px) saturate(160%)', // ģʽʹģЧ
            WebkitBackdropFilter: 'blur(14px) saturate(160%)', // ģʽʹģЧ
            border: isDarkMode ? '1px solid var(--glass-border)' : '1px solid rgba(0, 0, 0, 0.1)',
            boxShadow: 'none',
          }}
        >
          <button 
            onClick={() => { setIsComparing(false); onSwitchToOriginal(); }} 
            className={`flex-1 py-2 sm:py-[11px] md:py-[14px] flex items-center justify-center gap-2 sm:gap-3 transition-all ${isDarkMode ? 'text-white' : 'text-zinc-900'} min-h-[31px] sm:min-h-0`}
            style={{
              background: showOriginal && !isComparing ? (isDarkMode ? 'rgba(255, 255, 255, 0.0875)' : 'rgba(0, 0, 0, 0.0875)') : 'transparent',
            }}
            onMouseEnter={(e) => {
              if (!(showOriginal && !isComparing)) {
                e.currentTarget.style.background = isDarkMode ? 'rgba(255, 255, 255, 0.07)' : 'rgba(0, 0, 0, 0.07)';
              }
            }}
            onMouseLeave={(e) => {
              if (!(showOriginal && !isComparing)) {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <div className={`w-2 h-2 rounded-full ${showOriginal && !isComparing ? (isDarkMode ? 'bg-white' : 'bg-zinc-900') : (isDarkMode ? 'bg-white/40' : 'bg-zinc-900/40')}`}></div>
            <span className={`sidebar-btn-official-font sidebar-blue-box-smaller tracking-wider ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
              {tK('original')}
            </span>
          </button>

          <div className={`h-4 sm:h-6 md:h-7 w-px ${isDarkMode ? 'bg-white/20' : 'bg-zinc-900/20'}`}></div>
          
          <button 
            onClick={() => { setIsComparing(true); onSwitchToRendered(); }} 
            disabled={!renderedImage}
            className={`flex-1 py-2 sm:py-[11px] md:py-[14px] flex items-center justify-center gap-2 sm:gap-3 transition-all ${isDarkMode ? 'text-white' : 'text-zinc-900'} disabled:opacity-20 min-h-[31px] sm:min-h-0`}
            style={{
              background: isComparing ? (isDarkMode ? 'rgba(255, 255, 255, 0.0875)' : 'rgba(0, 0, 0, 0.0875)') : 'transparent',
            }}
            onMouseEnter={(e) => {
              if (!isComparing && renderedImage) {
                e.currentTarget.style.background = isDarkMode ? 'rgba(255, 255, 255, 0.07)' : 'rgba(0, 0, 0, 0.07)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isComparing) {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <div className={`w-2 h-2 rounded-full ${isComparing ? (isDarkMode ? 'bg-white' : 'bg-zinc-900') : (isDarkMode ? 'bg-white/40' : 'bg-zinc-900/40')}`}></div>
            <span className={`sidebar-btn-official-font sidebar-blue-box-smaller tracking-wider ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
              {tK('compare')}
            </span>
          </button>

          <div className={`h-4 sm:h-6 md:h-7 w-px ${isDarkMode ? 'bg-white/20' : 'bg-zinc-900/20'}`}></div>
          
          <button 
            onClick={() => { setIsComparing(false); onSwitchToRendered(); }} 
            disabled={!renderedImage && !showOriginal} 
            className={`flex-1 py-2 sm:py-[11px] md:py-[14px] flex items-center justify-center gap-2 sm:gap-3 transition-all ${isDarkMode ? 'text-white' : 'text-zinc-900'} disabled:opacity-20 min-h-[31px] sm:min-h-0`}
            style={{
              background: !showOriginal && !isComparing ? (isDarkMode ? 'rgba(255, 255, 255, 0.0875)' : 'rgba(0, 0, 0, 0.0875)') : 'transparent',
            }}
            onMouseEnter={(e) => {
              if (!(!showOriginal && !isComparing) && (renderedImage || showOriginal)) {
                e.currentTarget.style.background = isDarkMode ? 'rgba(255, 255, 255, 0.07)' : 'rgba(0, 0, 0, 0.07)';
              }
            }}
            onMouseLeave={(e) => {
              if (!(!showOriginal && !isComparing)) {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <div className={`w-2 h-2 rounded-full ${!showOriginal && !isComparing && (renderedImage || !showOriginal) ? (isDarkMode ? 'bg-white' : 'bg-zinc-900') : (isDarkMode ? 'bg-white/40' : 'bg-zinc-900/40')}`}></div>
            <span className={`sidebar-btn-official-font sidebar-blue-box-smaller tracking-wider ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
              {tK('results')}
            </span>
          </button>
        </div>

        {isScanning && (
          <div className="absolute inset-0 z-[200] bg-black/60 backdrop-blur-[2px] overflow-hidden">
             <div className="absolute left-0 right-0 h-[2px] bg-[#D7CDA1] z-[201] animate-[xray_2.0s_ease-in-out_infinite]"></div>
             <div className="absolute left-0 right-0 h-[150px] bg-gradient-to-b from-[#D7CDA1]/20 to-transparent z-[201] animate-[xray_2.0s_ease-in-out_infinite]"></div>
             <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="flex flex-col items-center gap-3">
                  <p className="text-[#D7CDA1] font-semibold text-[14px] text-center px-6 leading-relaxed">
                    {tK('scanning_identification')}
                  </p>
                  <div className="flex gap-1">
                    {[1,2,3].map(i => (
                      <div key={i} className={`w-1 h-1 bg-[#D7CDA1] rounded-full animate-bounce`} style={{ animationDelay: `${i*0.2}s` }}></div>
                    ))}
                  </div>
                </div>
             </div>
          </div>
        )}
      </div>

      {textCommandUnlocked ? renderChatDialog('bottom-6') : null}

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes xray { 
          0% { top: -10%; opacity: 0; }
          20% { opacity: 1; }
          80% { opacity: 1; }
          100% { top: 110%; opacity: 0; }
        }
        @keyframes fixture-selected-breathe {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 8px rgba(34, 197, 94, 0.65), 0 0 16px rgba(34, 197, 94, 0.35);
          }
          50% {
            transform: scale(1.22);
            box-shadow: 0 0 14px rgba(34, 197, 94, 0.95), 0 0 28px rgba(74, 222, 128, 0.55);
          }
        }
        .fixture-point-selected-breathe {
          animation: fixture-selected-breathe 1.65s ease-in-out infinite;
        }
      `}} />
    </div>
  );
};

export default Editor;
